// React
import React, { useEffect, useState } from 'react';

//Libs
import i18next from 'i18next';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

// Components
import PrimaryButton from '../button/primary-button';
import NumberInput from '../input/number-input';
import { SingleSelectWithSearch } from '../single-select-with-search';

// Images
import Icons from '../stock-avail/icons/Group.svg';
import Images from '../../../assets/img';

// Actions
import {
  getShippingRules,
  getShippingRuleMapping,
  saveShippingRuleMapping,
  updateShippingRuleMapping,
  deleteShippingRuleMapping
} from '../../../actions/shipping-action';

// Components
import ToggleButton from '../toggle-button';

// Css
import '../stock-avail/index.scss';
import './index.scss';

const loadMappingData = (
  mapping,
  options,
  setMappingId,
  setDevicesPerKg,
  setSelectedOption
) => {
  if (!mapping) return;
  setMappingId(mapping.id || null);
  setDevicesPerKg(
    mapping.quantityPerWeightUnit !== null &&
      mapping.quantityPerWeightUnit !== undefined
      ? String(mapping.quantityPerWeightUnit)
      : ''
  );
  const matched = options.find((option) => option.id === mapping.shippingRuleId);
  if (matched) setSelectedOption(matched);
};

const getInitialDevicesPerKg = (mapping) => {
  if (
    mapping.quantityPerWeightUnit !== null &&
    mapping.quantityPerWeightUnit !== undefined
  ) {
    return String(mapping.quantityPerWeightUnit);
  }
  return '';
};

const applyMappingInitialState = (
  mapping,
  options,
  setMappingId,
  setDevicesPerKg,
  setSelectedOption,
  setInitialOption,
  setInitialDevicesPerKg
) => {
  loadMappingData(
    mapping,
    options,
    setMappingId,
    setDevicesPerKg,
    setSelectedOption
  );
  if (!mapping) return;
  const matched = options.find((option) => option.id === mapping.shippingRuleId);
  setInitialOption(matched || null);
  setInitialDevicesPerKg(getInitialDevicesPerKg(mapping));
};

const buildOptions = (rules) =>
  rules.map((rule, idx) => ({
    id: rule.shippingRuleId || idx,
    label: rule.shippingRuleName,
    value: rule.shippingRuleName
  }));

const ShippingUpdate = ({ productId, closeModal, onSuccess }) => {
  const dispatch = useDispatch();

  const [deliveryTypeOptions, setDeliveryTypeOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [devicesPerKg, setDevicesPerKg] = useState('');
  const [devicesError, setDevicesError] = useState('');
  const [mappingId, setMappingId] = useState(null);
  const [initialOption, setInitialOption] = useState(null);
  const [initialDevicesPerKg, setInitialDevicesPerKg] = useState('');
  const [isShippingEnabled, setIsShippingEnabled] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    dispatch(getShippingRules()).then((rules) => {
      if (!Array.isArray(rules)) return;
      const options = buildOptions(rules);
      setDeliveryTypeOptions(options);
      dispatch(getShippingRuleMapping(productId)).then((mapping) => {
        if (mapping) setMappingId(mapping.id || null);
        if (mapping && mapping.isActive === true) {
          applyMappingInitialState(
            mapping,
            options,
            setMappingId,
            setDevicesPerKg,
            setSelectedOption,
            setInitialOption,
            setInitialDevicesPerKg
          );
          setIsShippingEnabled(true);
        }
      });
    });
  }, [productId]);

  const handleToggleShipping = (val) => {
    setIsShippingEnabled(val);
    if (!val) {
      setSelectedOption(null);
      setDevicesPerKg('');
      setDevicesError('');
    } else {
      setSelectedOption(initialOption);
      setDevicesPerKg(initialDevicesPerKg);
      setDevicesError('');
    }
  };

  const showSuccessSnackbar = () => {
    if (onSuccess) onSuccess(i18next.t('DYNAMIC_FORM.SHIPPING.UPDATE_SUCCESS'));
  };

  const handleUpdateSuccess = (res) => {
    if (res) {
      showSuccessSnackbar();
      closeModal();
    }
  };

  const handleSaveSuccess = (res) => {
    if (res) {
      setMappingId(res.id || null);
      showSuccessSnackbar();
      closeModal();
    }
  };

  const handleUpdate = async () => {
    if (!isShippingEnabled && mappingId) {
      setIsDeleting(true);
      const deleted = await dispatch(deleteShippingRuleMapping(mappingId));
      setIsDeleting(false);
      if (deleted) {
        showSuccessSnackbar();
        closeModal();
      }
      return;
    }
    const payload = {
      productId,
      shippingRuleId: selectedOption?.id,
      quantityPerWeightUnit: Number(devicesPerKg),
      isActive: true
    };
    if (mappingId) {
      dispatch(updateShippingRuleMapping(mappingId, { mappingId, ...payload })).then(
        handleUpdateSuccess
      );
    } else {
      dispatch(saveShippingRuleMapping(payload)).then(handleSaveSuccess);
    }
  };

  const isDisabled = () => {
    if (isDeleting) return true;
    if (!isShippingEnabled) return !mappingId;
    if (!selectedOption || !devicesPerKg || devicesError?.length > 0) return true;
    const deliveryUnchanged = selectedOption?.id === initialOption?.id;
    const devicesUnchanged = devicesPerKg === initialDevicesPerKg;
    return deliveryUnchanged && devicesUnchanged;
  };

  return (
    <section className='modal'>
      <button
        onClick={closeModal}
        className='modal__overlay'
        type='button'
        aria-label='Close modal'
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') closeModal();
        }}
      />
      <div className='modal__content'>
        <img
          className='modal__success'
          src={Icons}
          alt={i18next.t('DYNAMIC_FORM.ALTERNATE_TEXT.ARROW')}
        />
        <button
          className='modal__cancelIcon-btn'
          onClick={closeModal}
          type='button'
          aria-label={i18next.t('DYNAMIC_FORM.ALTERNATE_TEXT.CLOSE')}
        >
          <img
            className='modal__cancelIcon'
            src={Images.closeIcon}
            alt={i18next.t('DYNAMIC_FORM.ALTERNATE_TEXT.CLOSE')}
          />
        </button>
        <div className='alert__message'>
          <h1 className='shipping-alert__message__title -size-l'>
            {i18next.t('DYNAMIC_FORM.LABEL.SHIPPING_UPDATE')}
          </h1>
          <p className='shipping-update__message__subtitle '>
            {i18next.t('DYNAMIC_FORM.LABEL.SHIPPING_UPDATE_SUBTITLE')}
          </p>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              marginTop: '1.5rem'
            }}
          >
            <span style={{ fontSize: '0.875rem', color: 'var(--bosch-black)' }}>
              {i18next.t('DYNAMIC_FORM.SHIPPING.CHARGE_APPLICABLE')}
            </span>
            <ToggleButton
              id='shippingEnabled-toggle'
              name='shippingEnabled'
              label=''
              checked={isShippingEnabled}
              handleToggle={handleToggleShipping}
            />
          </div>
          {isShippingEnabled && (
            <>
              <div className='shipping-update__info-row'>
                <i className='a-icon boschicon-bosch-ic-alert-info shipping-update__info-icon' />
                <span className='shipping-update__info-text'>
                  {i18next.t(
                    'DYNAMIC_FORM.PRICING_PLAN.SHIPPING_INFORMATION.TOOLTIP'
                  )}
                </span>
              </div>
              <div className='modal__input' style={{ width: '35rem' }}>
                <SingleSelectWithSearch
                  displayName={i18next.t('DYNAMIC_FORM.SHIPPING.DELIVERY_TYPE')}
                  name='shippingDeliveryType'
                  placeholder={i18next.t(
                    'DYNAMIC_FORM.SHIPPING.SELECT_DELIVERY_TYPE'
                  )}
                  options={deliveryTypeOptions}
                  value={selectedOption}
                  validation={{ rule: { required: { isRequired: true } } }}
                  handleChange={(data) => setSelectedOption(data.value)}
                />
              </div>
              <div
                className='a-text-field modal__input'
                style={{ marginBottom: '6.5rem' }}
              >
                <NumberInput
                  displayName={i18next.t('DYNAMIC_FORM.SHIPPING.DEVICES_PER_KG')}
                  name='devicesPerKg'
                  value={devicesPerKg}
                  placeholder={i18next.t(
                    'DYNAMIC_FORM.SHIPPING.ENTER_DEVICES_PER_KG'
                  )}
                  handleChange={(data) => {
                    setDevicesPerKg(data.value);
                    setDevicesError(data.error || '');
                  }}
                  validation={{
                    rule: {
                      required: { isRequired: true },
                      regex: {
                        pattern: '^[1-9][0-9]*$'
                      }
                    }
                  }}
                  error={devicesError}
                />
              </div>
            </>
          )}
          <div className='modal__message'>
            <PrimaryButton
              onClick={handleUpdate}
              label={i18next.t('DYNAMIC_FORM.LABEL.UPDATE_CHANGES')}
              disabled={isDisabled()}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

ShippingUpdate.propTypes = {
  productId: PropTypes.string.isRequired,
  closeModal: PropTypes.func.isRequired,
  onSuccess: PropTypes.func
};

export default ShippingUpdate;
